/**
 * React hook wrapping MediaPipe FaceMesh for real-time face analysis.
 *
 * Loads the FaceMesh library from CDN via dynamic script injection and
 * processes video frames with time-based throttling (~15 FPS). Provides
 * head pose estimation, blink detection (EAR), smile detection (MAR),
 * pitch analysis, and ICAO-style occlusion checks.
 *
 * @module hooks/useFaceMesh
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

// ---------------------------------------------------------------------------
// MediaPipe global types (loaded from CDN, not an npm import)
// ---------------------------------------------------------------------------

/** Normalized 3D landmark as returned by MediaPipe FaceMesh. */
interface NormalizedLandmark {
  x: number;
  y: number;
  z: number;
}

interface FaceMeshResultsRaw {
  multiFaceLandmarks?: NormalizedLandmark[][];
}

interface FaceMeshInstance {
  setOptions(options: Record<string, unknown>): void;
  onResults(callback: (results: FaceMeshResultsRaw) => void): void;
  send(input: { image: HTMLVideoElement }): Promise<void>;
  close(): void;
}

interface FaceMeshConstructor {
  new (config: { locateFile: (file: string) => string }): FaceMeshInstance;
}

declare global {
  interface Window {
    FaceMesh?: FaceMeshConstructor;
  }
}

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface HeadPose {
  /** Yaw estimate. Positive = turned right, negative = turned left. */
  yaw: number;
  /** Pitch ratio. ~1.0 = straight, <0.7 = looking up, >1.4 = looking down. */
  pitch: number;
}

export interface FaceMeshResult {
  /** Whether the FaceMesh model is loaded and processing frames. */
  isReady: boolean;
  /** Raw face landmarks from the latest frame, or null if no face detected. */
  landmarks: NormalizedLandmark[] | null;
  /** Head pose estimates, or null if no face detected. */
  headPose: HeadPose | null;
  /** Eye Aspect Ratio (Soukupova & Cech). Lower = more closed. */
  earScore: number;
  /** Mouth Aspect Ratio. Higher = mouth more open (smile/speech). */
  marScore: number;
  /** Whether an ICAO-style occlusion issue is detected. */
  isOccluded: boolean;
  /** Human-readable occlusion message, or null if no issue. */
  occlusionMessage: string | null;
  /** Error message if FaceMesh failed to load. */
  error: string | null;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// MediaPipe FaceMesh is self-hosted under /static/mediapipe/ for Subresource
// Integrity (SRI) and CSP tightening. The pinned upstream version is
// @mediapipe/face_mesh@0.4.1633559619; the download commands and hash refresh
// procedure are documented in public/static/mediapipe/README.md.
const FACE_MESH_SCRIPT_URL = '/static/mediapipe/face_mesh.js';
const FACE_MESH_BASE_PATH = '/static/mediapipe/';
// SHA384 integrity hash for face_mesh.js (@0.4.1633559619).
// Recompute with:
//   openssl dgst -sha384 -binary apps/portal-web/public/static/mediapipe/face_mesh.js | openssl base64 -A
const FACE_MESH_SCRIPT_INTEGRITY =
  'sha384-nKiz5QrpRlMQLw5nrZcprT7N9vmmAcIgV8TuGuep4x91V4JIPsXa+D44Wxj0guoa';
const THROTTLE_MS = 65; // ~15 FPS

// Landmark indices (MediaPipe FaceMesh canonical model)
const IDX_NOSE = 1;
const IDX_LEFT_CHEEK = 234;
const IDX_RIGHT_CHEEK = 454;
const IDX_FOREHEAD = 10;
const IDX_CHIN = 152;

// EAR landmarks (left eye)
const IDX_EYE_INNER = 33;
const IDX_EYE_UPPER_1 = 160;
const IDX_EYE_UPPER_2 = 158;
const IDX_EYE_OUTER = 133;
const IDX_EYE_LOWER_2 = 153;
const IDX_EYE_LOWER_1 = 144;

// MAR landmarks
const IDX_MOUTH_TOP = 13;
const IDX_MOUTH_BOTTOM = 14;
const IDX_MOUTH_LEFT = 61;
const IDX_MOUTH_RIGHT = 291;

// Occlusion thresholds
const OCCLUSION_FOREHEAD_MIN_Y = 0.08;
const OCCLUSION_FACE_HEIGHT_MIN = 0.40;
const OCCLUSION_FACE_HEIGHT_MAX = 0.85;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function dist(a: NormalizedLandmark, b: NormalizedLandmark): number {
  return Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);
}

/**
 * Load a script from a URL and resolve when it finishes loading.
 * Deduplicates if the script element already exists. When `integrity` is
 * provided, the browser will enforce a SHA384 Subresource Integrity check
 * and refuse to execute the script if the hash does not match — this guards
 * against supply-chain tampering of the self-hosted MediaPipe bundle.
 */
function loadScript(src: string, integrity?: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      // Script tag exists. If FaceMesh is already on window, resolve immediately.
      if (window.FaceMesh) {
        resolve();
        return;
      }
      // Otherwise wait for load event on the existing element.
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () =>
        reject(new Error('Failed to load FaceMesh script')),
      );
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    // Same-origin asset, but set crossorigin so integrity checks work
    // uniformly and any future CDN migration does not silently skip SRI.
    script.crossOrigin = 'anonymous';
    if (integrity) {
      script.integrity = integrity;
    }
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load FaceMesh script'));
    document.head.appendChild(script);
  });
}

// ---------------------------------------------------------------------------
// Compute functions (pure, no side effects)
// ---------------------------------------------------------------------------

function computeHeadPose(lm: NormalizedLandmark[]): HeadPose {
  const nose = lm[IDX_NOSE];
  const leftCheek = lm[IDX_LEFT_CHEEK];
  const rightCheek = lm[IDX_RIGHT_CHEEK];
  const forehead = lm[IDX_FOREHEAD];
  const chin = lm[IDX_CHIN];

  const distLeft = dist(nose, leftCheek);
  const distRight = dist(nose, rightCheek);

  // Yaw: positive = turned right (left cheek more visible)
  const yaw = distLeft / (distRight || 0.001);

  // Pitch: ratio of forehead-nose to nose-chin distance
  const topDist = Math.abs(nose.y - forehead.y);
  const bottomDist = Math.abs(chin.y - nose.y);
  const pitch = topDist / (bottomDist || 0.001);

  return { yaw, pitch };
}

function computeEAR(lm: NormalizedLandmark[]): number {
  const p1 = lm[IDX_EYE_INNER];
  const p2 = lm[IDX_EYE_UPPER_1];
  const p3 = lm[IDX_EYE_UPPER_2];
  const p4 = lm[IDX_EYE_OUTER];
  const p5 = lm[IDX_EYE_LOWER_2];
  const p6 = lm[IDX_EYE_LOWER_1];

  const horizontal = dist(p1, p4);
  if (horizontal === 0) return 0;

  return (dist(p2, p6) + dist(p3, p5)) / (2.0 * horizontal);
}

function computeMAR(lm: NormalizedLandmark[]): number {
  const top = lm[IDX_MOUTH_TOP];
  const bottom = lm[IDX_MOUTH_BOTTOM];
  const left = lm[IDX_MOUTH_LEFT];
  const right = lm[IDX_MOUTH_RIGHT];

  const horizontal = dist(left, right);
  if (horizontal === 0) return 0;

  return dist(top, bottom) / horizontal;
}

function computeOcclusion(lm: NormalizedLandmark[]): {
  isOccluded: boolean;
  message: string | null;
} {
  const forehead = lm[IDX_FOREHEAD];
  const chin = lm[IDX_CHIN];
  const faceHeight = Math.abs(forehead.y - chin.y);

  if (forehead.y < OCCLUSION_FOREHEAD_MIN_Y) {
    return {
      isOccluded: true,
      message: 'Face partially out of frame. Remove hats and center your face.',
    };
  }
  if (faceHeight < OCCLUSION_FACE_HEIGHT_MIN) {
    return {
      isOccluded: true,
      message: 'Move closer to the camera.',
    };
  }
  if (faceHeight > OCCLUSION_FACE_HEIGHT_MAX) {
    return {
      isOccluded: true,
      message: 'Move further from the camera.',
    };
  }

  return { isOccluded: false, message: null };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

const INITIAL_STATE: FaceMeshResult = {
  isReady: false,
  landmarks: null,
  headPose: null,
  earScore: 0,
  marScore: 0,
  isOccluded: false,
  occlusionMessage: null,
  error: null,
};

export function useFaceMesh(
  videoRef: RefObject<HTMLVideoElement | null>,
  enabled: boolean,
): FaceMeshResult {
  const [state, setState] = useState<FaceMeshResult>(INITIAL_STATE);

  // Mutable refs for the animation loop to avoid stale closures.
  const faceMeshRef = useRef<FaceMeshInstance | null>(null);
  const lastProcessingTimeRef = useRef(0);
  const isProcessingRef = useRef(false);
  const rafIdRef = useRef<number | null>(null);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  // Results callback — update state from FaceMesh output.
  const onResults = useCallback((results: FaceMeshResultsRaw) => {
    if (
      !results.multiFaceLandmarks ||
      results.multiFaceLandmarks.length === 0
    ) {
      setState((prev) => ({
        ...prev,
        landmarks: null,
        headPose: null,
        earScore: 0,
        marScore: 0,
        isOccluded: false,
        occlusionMessage: null,
      }));
      return;
    }

    const lm = results.multiFaceLandmarks[0];
    const headPose = computeHeadPose(lm);
    const earScore = computeEAR(lm);
    const marScore = computeMAR(lm);
    const occlusion = computeOcclusion(lm);

    setState((prev) => ({
      ...prev,
      landmarks: lm,
      headPose,
      earScore,
      marScore,
      isOccluded: occlusion.isOccluded,
      occlusionMessage: occlusion.message,
    }));
  }, []);

  // Frame processing loop using requestAnimationFrame with time-based throttle.
  const processFrame = useCallback(() => {
    if (!enabledRef.current) return;

    const video = videoRef.current;
    const fm = faceMeshRef.current;

    if (video && fm && video.readyState >= 2) {
      const now = Date.now();
      if (now - lastProcessingTimeRef.current >= THROTTLE_MS && !isProcessingRef.current) {
        lastProcessingTimeRef.current = now;
        isProcessingRef.current = true;
        fm.send({ image: video }).finally(() => {
          isProcessingRef.current = false;
        });
      }
    }

    rafIdRef.current = requestAnimationFrame(processFrame);
  }, [videoRef]);

  // Main effect: load FaceMesh from CDN and start processing.
  useEffect(() => {
    if (!enabled) {
      // Stop the loop and reset state when disabled.
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      setState(INITIAL_STATE);
      return;
    }

    let cancelled = false;

    async function init() {
      try {
        // Load the self-hosted FaceMesh loader with SRI enforcement.
        await loadScript(FACE_MESH_SCRIPT_URL, FACE_MESH_SCRIPT_INTEGRITY);

        if (cancelled) return;

        if (!window.FaceMesh) {
          setState((prev) => ({
            ...prev,
            error: 'FaceMesh library failed to initialize.',
          }));
          return;
        }

        // Create and configure the FaceMesh instance. locateFile MUST point
        // to our self-hosted bundle so the WASM / packed-assets fetches are
        // same-origin and covered by the app's CSP.
        const fm = new window.FaceMesh({
          locateFile: (file: string) => `${FACE_MESH_BASE_PATH}${file}`,
        });

        fm.setOptions({
          maxNumFaces: 1,
          refineLandmarks: true,
          minDetectionConfidence: 0.5,
          minTrackingConfidence: 0.5,
        });

        fm.onResults(onResults);

        faceMeshRef.current = fm;

        if (!cancelled) {
          setState((prev) => ({ ...prev, isReady: true, error: null }));
          // Start the frame processing loop.
          rafIdRef.current = requestAnimationFrame(processFrame);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const message =
            err instanceof Error ? err.message : 'Failed to load FaceMesh.';
          setState((prev) => ({ ...prev, error: message }));
        }
      }
    }

    init();

    return () => {
      cancelled = true;
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      if (faceMeshRef.current) {
        try {
          faceMeshRef.current.close();
        } catch {
          // FaceMesh close may throw if already disposed.
        }
        faceMeshRef.current = null;
      }
    };
  }, [enabled, onResults, processFrame]);

  return state;
}
